import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { getRouteMeta } from './RouteMeta';

/** Review fix F7: the first focusable element on every page shell. Moves focus to `#main-content`
 * (which carries tabIndex={-1}) rather than relying on fragment-navigation focus behavior. */
export function SkipLink({ targetId = 'main-content' }: { targetId?: string }) {
  const { t } = useTranslation();
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    const target = document.getElementById(targetId);
    if (!target) return;
    e.preventDefault();
    target.focus();
    target.scrollIntoView();
  };
  return (
    <a href={`#${targetId}`} id="skip-link" className="skip-link" onClick={onClick}>
      {t('a11y.skipToContent')}
    </a>
  );
}

/** Review fix F7: a single polite live region that announces the new page's title after every
 * client-side route change (a SPA navigation is otherwise silent for screen-reader users). The
 * initial page load is not announced -- the browser already reads the document title then. */
export function RouteAnnouncer() {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const [message, setMessage] = useState('');
  const isFirst = useRef(true);

  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    setMessage('');
    const id = window.setTimeout(() => setMessage(getRouteMeta(pathname).title), 150);
    return () => window.clearTimeout(id);
  }, [pathname, i18n.language]);

  return (
    <div className="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
      {message}
    </div>
  );
}
